import React, { useState, useMemo, useLayoutEffect, useRef } from 'react';
import { APEXO_VIEWBOX, apexoPaths } from './apexoSvg';
import { useCareFlow360Store } from '../hooks/useCareFlow360Store';
import ApexoToothStateWheel from './ApexoToothStateWheel';
import { labelToColor, txOptions, labelToIcon } from './txOptions';
import { careflow360Api } from '../api/careflow360Api';
import { ChartEvent } from './types';

interface ApexoToothChartProps {
  visitId: number | null;
  patientId: string;
  onOpenBilling?: () => void;
  onOpenDiagnosis?: () => void;
  onOpenTreatment?: () => void;
}

export default function ApexoToothChart({ visitId, patientId }: ApexoToothChartProps) {
  const { 
    events, 
    visitVersion,
    selectedTeeth, 
    toggleToothSelection, 
    mode, 
    showPrimary,
    showPermanent,
    notation,
    addEvent,
    updateEvent,
    removeEvent,
    clearSelection,
    undoLastEvent,
    setNotation,
    setMode,
    togglePrimary,
    togglePermanent,
    setEvents,
    setVisitVersion
  } = useCareFlow360Store();

  const [wheelTooth, setWheelTooth] = useState<number | null>(null);
  const toothPathRefs = useRef<Record<number, SVGPathElement | null>>({});
  const [toothBounds, setToothBounds] = useState<Record<number, { x: number; y: number; bx: number; by: number; bw: number; bh: number }>>({});

  const adultTeeth = [
    18, 17, 16, 15, 14, 13, 12, 11,   21, 22, 23, 24, 25, 26, 27, 28,
    48, 47, 46, 45, 44, 43, 42, 41,   31, 32, 33, 34, 35, 36, 37, 38,
  ];

  const primaryTeeth = [
    55, 54, 53, 52, 51,   61, 62, 63, 64, 65,
    85, 84, 83, 82, 81,   71, 72, 73, 74, 75,
  ];

  const displayTeeth = [
    ...(showPermanent ? adultTeeth : []),
    ...(showPrimary ? primaryTeeth : [])
  ];

  useLayoutEffect(() => {
    const next: Record<number, { x: number; y: number; bx: number; by: number; bw: number; bh: number }> = {};
    for (const toothId of displayTeeth) {
      const node = toothPathRefs.current[toothId];
      if (!node) continue;
      try {
        const box = node.getBBox();
        next[toothId] = { 
          x: box.x + box.width / 2, 
          y: box.y + box.height / 2,
          bx: box.x,
          by: box.y,
          bw: box.width,
          bh: box.height
        };
      } catch (e) {
        // Ignore edge-case SVG bbox calculation failures
      }
    }
    const oldKeys = Object.keys(toothBounds);
    const newKeys = Object.keys(next);
    if (oldKeys.length !== newKeys.length) {
      setToothBounds(next);
      return;
    }
    for (const key of newKeys) {
      const id = Number(key);
      const prev = toothBounds[id];
      const current = next[id];
      if (!prev || !current || prev.x !== current.x || prev.y !== current.y) {
        setToothBounds(next);
        return;
      }
    }
  }, [events.length, toothBounds, showPrimary, showPermanent, displayTeeth.length]);

  const handleToothClick = (toothId: number, e: React.MouseEvent) => {
    e.preventDefault();
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      toggleToothSelection(toothId);
    } else {
      if (!selectedTeeth.includes(toothId)) {
        clearSelection();
        toggleToothSelection(toothId);
      }
      setWheelTooth(toothId);
    }
  };

  const handleWheelSet = async (label: string | null) => {
    if (!wheelTooth) return;

    const teethToApply =
      selectedTeeth.length > 0 && selectedTeeth.includes(wheelTooth) ? selectedTeeth : [wheelTooth];

    let workingVersion = visitVersion;
    const reloadFromServer = async () => {
      if (!visitId) return;
      const latest = await careflow360Api.fetchChartEvents(visitId, patientId);
      setEvents(latest.events);
      setVisitVersion(latest.visitVersion);
    };

    if (label === null) {
      // DELETE scenario
      // Find latest event for teethToApply and remove
      let shouldStop = false;
      for (const t of teethToApply) {
        if (shouldStop) break;
        // Find latest event for this tooth that matches current mode
        const toothEvents = events.filter(
          (e) =>
            e.toothId === t &&
            !e.isHistorical &&
            (e.domain || 'treatment') === 'treatment'
        );
        toothEvents.sort((a, b) => b.timestamp - a.timestamp);
        const latest = toothEvents.find(
          (e) => mode === 'both' || e.type === mode || e.type === 'both'
        );
        
        if (latest) {
          removeEvent(latest.id);
        }
      }
      setWheelTooth(null);
      return;
    }

    const option = txOptions.find(o => o.label === label);

    for (const toothId of teethToApply) {
      const optimisticEvent: ChartEvent = {
        id: crypto.randomUUID(),
        timestamp: Date.now(),
        persistedId: undefined,
        toothId,
        label,
        type: option ? option.type : 'both', 
        visitId: visitId || undefined,
        isHistorical: false,
        domain: 'treatment',
      };

      addEvent(optimisticEvent); // optimistic

      // AUTOMATIC BILLING: If in treatment mode, record to backend
      if (label && mode === 'treatment' && visitId) {
        const masterLabel = option?.masterLabel || label;
        // Fire and forget or handle error? Usually we want to at least try.
        void careflow360Api.recordProcedureFromChart(visitId, patientId, {
          procedure_type: masterLabel,
          tooth_no: String(toothId),
          visitVersion: workingVersion
        }).catch((err: any) => console.error("Auto-billing failed", err));
      }
    }

    setWheelTooth(null);
  };

  const getToothColor = (toothId: number) => {
    // Find latest event for this tooth that matches current mode (or both)
    const toothEvents = events.filter(e => e.toothId === toothId);
    if (toothEvents.length === 0) return { fill: 'white', isHistorical: false };
    
    // Process current vs historical
    const currentNotes = toothEvents.filter(e => !e.isHistorical);
    const historicalNotes = toothEvents.filter(e => e.isHistorical);
    
    const sortByTime = (a: ChartEvent, b: ChartEvent) => b.timestamp - a.timestamp;
    currentNotes.sort(sortByTime);
    historicalNotes.sort(sortByTime);
    
    // Priority 1: Current note matches mode
    const relevantCurrent = currentNotes.find(
      (e) => mode === 'both' || e.type === mode || e.type === 'both'
    );
    if (relevantCurrent) return { fill: labelToColor(relevantCurrent.label), isHistorical: false };

    // Priority 2: Historical note matches mode
    const relevantHistorical = historicalNotes.find(
      (e) => mode === 'both' || e.type === mode || e.type === 'both'
    );
    if (relevantHistorical) {
      const c = labelToColor(relevantHistorical.label);
      // subdued color by appending alpha 80 (50%)
      return { fill: c + '80', isHistorical: true };
    }

    return { fill: 'white', isHistorical: false };
  };

  const legendItems = useMemo(() => {
    const usage = new Map<
      string,
      { key: string; label: string; color: string; isHistorical: boolean }
    >();
    let hasHistory = false;

    events.forEach((event) => {
      const matchesMode =
        mode === 'both' || event.type === mode || event.type === 'both';
      if (!matchesMode) return;

      const normalizedLabel = txOptions.some((option) => option.label === event.label)
        ? event.label
        : 'other';
      const key = `${normalizedLabel}|${event.isHistorical ? '1' : '0'}`;

      if (!usage.has(key)) {
        const baseColor = labelToColor(normalizedLabel);
        usage.set(key, {
          key,
          label: normalizedLabel,
          color: event.isHistorical ? `${baseColor}80` : baseColor,
          isHistorical: Boolean(event.isHistorical),
        });
      }

      if (event.isHistorical) hasHistory = true;
    });

    return { items: Array.from(usage.values()), hasHistory };
  }, [events, mode]);

  return (
    <div className="apexo-chart-wrapper">
      <div className="apexo-chart-toolbar">
         <div className="mode-toggle">
            <button className={mode === 'state' ? 'active' : ''} onClick={() => setMode('state')}>Conditions</button>
            <button className={mode === 'treatment' ? 'active' : ''} onClick={() => setMode('treatment')}>Treatments</button>
         </div>
         <div className="notation-toggle mode-toggle">
            <button className={notation === 'iso' ? 'active' : ''} onClick={() => setNotation('iso')}>ISO</button>
            <button className={notation === 'palmer' ? 'active' : ''} onClick={() => setNotation('palmer')}>Palmer</button>
            <button className={notation === 'universal' ? 'active' : ''} onClick={() => setNotation('universal')}>Universal</button>
         </div>
         <div className="chart-controls mode-toggle">
            <button className="primary-toggle" onClick={togglePrimary}>
              {showPrimary ? '👶 Hide Primary' : '👶 Show Primary'}
            </button>
            <button className="permanent-toggle" onClick={togglePermanent}>
              {showPermanent ? '🦷 Hide Permanent' : '🦷 Show Permanent'}
            </button>
            <button onClick={undoLastEvent} disabled={events.length === 0}>↶ Undo</button>
         </div>
      </div>

      <div className="apexo-svg-container">
        <svg viewBox={APEXO_VIEWBOX} className="apexo-dental-chart-svg">
          {displayTeeth.map(toothId => {
            const d = apexoPaths[toothId];
            if (!d) return null;
            const isSelected = selectedTeeth.includes(toothId);
            const colorData = getToothColor(toothId);

            const bounds = toothBounds[toothId];
            const latestEvent = events
              .filter(e => e.toothId === toothId && (mode === 'both' || e.type === mode || e.type === 'both'))
              .sort((a, b) => b.timestamp - a.timestamp)[0];

            return (
              <g key={toothId} className="apexo-tooth-group" style={{ cursor: 'pointer' }} onClick={(e) => handleToothClick(toothId, e)}>
                {bounds && <rect x={bounds.bx} y={bounds.by} width={bounds.bw} height={bounds.bh} fill="transparent" pointerEvents="all" />}
                <path
                  ref={(node) => {
                    toothPathRefs.current[toothId] = node;
                  }}
                  d={d}
                  fill={colorData.fill}
                  stroke={isSelected ? '#2563eb' : '#94a3b8'}
                  strokeWidth={isSelected ? 3 : 1}
                  className="apexo-tooth-path"
                />
                {latestEvent && bounds && (
                  <text
                    x={bounds.x}
                    y={bounds.y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="apexo-tooth-icon"
                    style={{ fontSize: '12px', pointerEvents: 'none', userSelect: 'none', fontFamily: 'DentalIcons, sans-serif' }}
                  >
                    {labelToIcon(latestEvent.label)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Labels Left/Right */}
        <div className="orientation-labels">
          <span className="label-right">R</span>
          <span className="label-left">L</span>
        </div>
      </div>

      {/* Legend */}
      {legendItems.items.length > 0 && (
        <div className="apexo-legend">
          {legendItems.items.map((item) => (
            <div key={item.key} className={`legend-chip ${item.isHistorical ? 'historical' : ''}`}>
              <span className="legend-swatch" style={{ background: item.color }}></span>
              {item.label}
            </div>
          ))}
          {legendItems.hasHistory && (
            <div className="legend-chip history-marker">
              <span className="legend-icon">⏳</span> Historical Record
            </div>
          )}
        </div>
      )}

      {wheelTooth && (
        <ApexoToothStateWheel
          toothId={wheelTooth as number}
          type={mode === 'both' ? 'both' : mode}
          onSet={handleWheelSet}
          onClose={() => setWheelTooth(null)}
        />
      )}
    </div>
  );
}
